import React, { useEffect } from 'react'
import {
  DimmingProfileEditorActions,
  DimmingProfileEditorState,
} from '../types/presenter.types'
import { LEVEL_INACTIVE } from '../types/dimmingProfile.types'
import { formatMinutes } from '../utils/stepMath'
import { DimmingProfileUiStrings } from '../i18n/dimmingProfileI18n'

interface ProfileCurveProps {
  state: DimmingProfileEditorState
  actions: DimmingProfileEditorActions
  svgRef: React.RefObject<SVGSVGElement>
  strings: DimmingProfileUiStrings
  // Lecture seule : ni ajout au clic, ni glisser ; la sélection reste possible
  // pour lire la valeur d'un palier.
  readOnly?: boolean
}

const COLOR_AXIS = '#9ca3af'
const COLOR_GRID = '#eef2f7'
const COLOR_GRID_MAJOR = '#dde3ec'
const COLOR_CURVE = '#2563eb'
const COLOR_HANDLE = '#2563eb'
const COLOR_HANDLE_SELECTED = '#1d4ed8'
const COLOR_INACTIVE = '#9ca3af'

// X grid lines every 2 hours (major) plus hourly ticks.
const HOUR_TICKS = [0, 3, 6, 9, 12, 15, 18, 21, 24]
const LEVEL_TICKS = [0, 25, 50, 75, 100]

const ProfileCurve: React.FC<ProfileCurveProps> = ({
  state,
  actions,
  svgRef,
  strings,
  readOnly = false,
}) => {
  const { geometry: g, steps, selectedStepIndex, draggingStepIndex } = state
  const handleCursor = readOnly ? 'pointer' : 'grab'
  const startDrag = (e: React.PointerEvent, i: number) => {
    e.stopPropagation()
    if (!readOnly) actions.beginDrag(i)
  }

  // Drag is driven by window listeners so it keeps tracking outside the SVG.
  useEffect(() => {
    if (draggingStepIndex === null) return
    const handleMove = (e: PointerEvent) => actions.dragTo(e.clientX, e.clientY)
    const handleUp = () => actions.endDrag()
    window.addEventListener('pointermove', handleMove)
    window.addEventListener('pointerup', handleUp)
    return () => {
      window.removeEventListener('pointermove', handleMove)
      window.removeEventListener('pointerup', handleUp)
    }
  }, [draggingStepIndex, actions])

  const baselineY = actions.levelToY(0)

  // Build the step-curve polyline points. A "step" curve holds the previous
  // level until the next step time (horizontal then vertical).
  const buildStepPath = (): string => {
    if (steps.length === 0) return ''
    const pts: string[] = []
    // Start at the first step's level from time 0.
    const first = steps[0]
    const firstY = actions.levelToY(first.dimming_level)
    pts.push(`M ${actions.timeToX(0)} ${firstY}`)
    for (let i = 0; i < steps.length; i++) {
      const s = steps[i]
      const x = actions.timeToX(s.step_time_minutes)
      const y = actions.levelToY(s.dimming_level)
      // Horizontal to this step's x at the previous level, then vertical.
      pts.push(`L ${x} ${pts.length === 1 ? firstY : actions.levelToY(steps[i - 1].dimming_level)}`)
      pts.push(`L ${x} ${y}`)
    }
    // Hold the last level to end of day.
    const last = steps[steps.length - 1]
    pts.push(`L ${actions.timeToX(1430)} ${actions.levelToY(last.dimming_level)}`)
    return pts.join(' ')
  }

  return (
    <div style={{ width: '100%', maxWidth: 640 }}>
      <svg
        ref={svgRef}
        data-testid="profile-curve"
        viewBox={`0 0 ${g.width} ${g.height}`}
        style={{
          width: '100%',
          height: 'auto',
          touchAction: 'none',
          userSelect: 'none',
          background: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: 6,
        }}
        onClick={(e) => {
          // Only treat clicks on the background (not on a handle) as "add".
          if (readOnly) return
          if ((e.target as Element).getAttribute('data-handle') === '1') return
          actions.handlePlotClick(e.clientX, e.clientY)
        }}
      >
        {/* Plot border */}
        <rect
          x={g.marginLeft}
          y={g.marginTop}
          width={g.plotWidth}
          height={g.plotHeight}
          fill="none"
          stroke={COLOR_AXIS}
          strokeWidth={1}
        />

        {/* Horizontal level grid + labels */}
        {LEVEL_TICKS.map((lvl) => {
          const y = actions.levelToY(lvl)
          return (
            <g key={`lvl-${lvl}`}>
              <line
                x1={g.marginLeft}
                y1={y}
                x2={g.marginLeft + g.plotWidth}
                y2={y}
                stroke={lvl === 0 ? COLOR_GRID_MAJOR : COLOR_GRID}
                strokeWidth={1}
              />
              <text
                x={g.marginLeft - 8}
                y={y + 4}
                textAnchor="end"
                fontSize={11}
                fill="#6b7280"
              >
                {lvl}
              </text>
            </g>
          )
        })}

        {/* Vertical time grid + labels (every 3h) */}
        {HOUR_TICKS.map((h) => {
          const minutes = Math.min(1430, h * 60)
          const x = actions.timeToX(minutes)
          return (
            <g key={`h-${h}`}>
              <line
                x1={x}
                y1={g.marginTop}
                x2={x}
                y2={g.marginTop + g.plotHeight}
                stroke={COLOR_GRID_MAJOR}
                strokeWidth={1}
              />
              <text
                x={x}
                y={g.marginTop + g.plotHeight + 16}
                textAnchor="middle"
                fontSize={11}
                fill="#6b7280"
              >
                {String(h).padStart(2, '0')}:00
              </text>
            </g>
          )
        })}

        {/* Axis labels */}
        <text
          x={g.marginLeft + g.plotWidth / 2}
          y={g.height - 2}
          textAnchor="middle"
          fontSize={12}
          fill="#374151"
        >
          {strings.timeAxis}
        </text>

        {/* Step curve */}
        {steps.length > 0 && (
          <path
            d={buildStepPath()}
            fill="none"
            stroke={COLOR_CURVE}
            strokeWidth={2}
          />
        )}

        {/* Step handles */}
        {steps.map((s, i) => {
          const x = actions.timeToX(s.step_time_minutes)
          const inactive = s.dimming_level === LEVEL_INACTIVE
          const y = inactive ? baselineY : actions.levelToY(s.dimming_level)
          const isSelected = i === selectedStepIndex
          return (
            <g key={`step-${i}`}>
              {/* Vertical guide to baseline for readability */}
              <line
                x1={x}
                y1={y}
                x2={x}
                y2={baselineY}
                stroke={inactive ? COLOR_INACTIVE : COLOR_CURVE}
                strokeWidth={1}
                strokeDasharray={inactive ? '3 3' : undefined}
                opacity={0.4}
              />
              {/* Handle: inactive uses a hollow diamond, active a filled circle */}
              {inactive ? (
                <rect
                  data-handle="1"
                  x={x - 6}
                  y={y - 6}
                  width={12}
                  height={12}
                  transform={`rotate(45 ${x} ${y})`}
                  fill="#fff"
                  stroke={COLOR_INACTIVE}
                  strokeWidth={2}
                  style={{ cursor: handleCursor }}
                  onPointerDown={(e) => startDrag(e, i)}
                  onClick={(e) => {
                    e.stopPropagation()
                    actions.selectStep(i)
                  }}
                />
              ) : (
                <circle
                  data-handle="1"
                  cx={x}
                  cy={y}
                  r={isSelected ? 7 : 5.5}
                  fill={isSelected ? COLOR_HANDLE_SELECTED : COLOR_HANDLE}
                  stroke="#fff"
                  strokeWidth={2}
                  style={{ cursor: handleCursor }}
                  onPointerDown={(e) => startDrag(e, i)}
                  onClick={(e) => {
                    e.stopPropagation()
                    actions.selectStep(i)
                  }}
                />
              )}
              {/* Tooltip-ish label for the selected/dragged step */}
              {(isSelected || i === draggingStepIndex) && (
                <text
                  data-handle="1"
                  x={x}
                  y={y - 12}
                  textAnchor="middle"
                  fontSize={11}
                  fontWeight={600}
                  fill="#111827"
                >
                  {formatMinutes(s.step_time_minutes)} ·{' '}
                  {inactive ? strings.inactive : `${s.dimming_level}%`}
                </text>
              )}
            </g>
          )
        })}

        {/* Y axis caption (rotated) */}
        <text
          x={12}
          y={g.marginTop + g.plotHeight / 2}
          textAnchor="middle"
          fontSize={12}
          fill="#374151"
          transform={`rotate(-90 12 ${g.marginTop + g.plotHeight / 2})`}
        >
          {strings.levelAxis}
        </text>
      </svg>
      <p style={{ fontSize: 12, color: '#6b7280', margin: '6px 2px 0' }}>
        {strings.snapHint}
        {steps.length === 0 && !readOnly ? ` ${strings.noSteps}` : ''}
      </p>
    </div>
  )
}

export default ProfileCurve
